/**
 * IntegrationsSection — the real cards section (S6): three IntegrationCard
 * instances composed from useClientCapabilities + the agency OAuth status
 * query + the tier query, inside the shared SectionPage frame.
 *
 *  - Google Ads (the cascade card): the pill is RESOLVED from the two
 *    upstreams (agency connection × customer mapping) — never the legacy
 *    green-title contradiction. Absent entirely when the workspace's plan
 *    is SEO-only; a ?focus=google-ads deep link on an SEO plan gets the
 *    honest empty line (COPY.cards.adsPlanEmpty), not a card.
 *  - WordPress: verified flag ALWAYS passed (never a bare "Connected");
 *    disconnect goes through the lock-in dialog that NAMES the blast
 *    radius. The connect flow lives in the on-demand detail.
 *  - Google Docs: the two export fields extracted as a small detail form
 *    with its own Save.
 *
 * OAuth-return handling (EXTRACTED from GoogleAdsOAuthCard): the toast
 * wording is legacy-verbatim; a just-connected line is LATCHED into the
 * cascade's Connection layer until the status query confirms connected
 * (the pill + the persistent line are the record; the toast is not), and
 * an OAuth error latches a dismissible warning line.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Switch, message } from 'antd';
import { SectionPage } from './SectionPage';
import {
  DisconnectLockInDialog,
  IntegrationCard,
} from '@/components/settings/IntegrationCard';
import { staleSuffixFor } from '@/components/settings/StatusPill';
import { COPY } from '@/lib/copyTokens';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { isElevatedRole } from '@/lib/roles';
import {
  invalidateClientCapabilities,
  notifyWordPressConnectionWritten,
  useClientCapabilities,
} from '@/hooks/useClientCapabilities';
import { useUpdateTenantSettings } from '@/hooks/useTenantSettings';
import { apiService } from '@/services/api';
import type { TenantSettings } from '@/types/tenant';
import { formatGoogleAdsCustomerId } from './integrations/googleAdsIds';
import { GoogleAdsCascadeDetail } from './integrations/GoogleAdsCascadeDetail';
import { WordPressConnectionDetail } from './integrations/WordPressConnectionDetail';

/** Google Docs detail — the two export fields + their own Save. */
function GoogleDocsDetail({
  tenantId,
  tenantSettings,
}: {
  tenantId: string;
  tenantSettings: TenantSettings | null | undefined;
}) {
  const queryClient = useQueryClient();
  const updateSettings = useUpdateTenantSettings();
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      gdocs_auto_upload_enabled: tenantSettings?.gdocs_auto_upload_enabled,
      gdocs_folder_id: tenantSettings?.gdocs_folder_id,
    });
  }, [tenantSettings, form]);

  return (
    <Form
      form={form}
      layout="vertical"
      size="small"
      onFinish={async (values: {
        gdocs_auto_upload_enabled?: boolean;
        gdocs_folder_id?: string;
      }) => {
        await updateSettings.mutateAsync({
          website: tenantId,
          data: {
            gdocs_auto_upload_enabled: values.gdocs_auto_upload_enabled,
            gdocs_folder_id: values.gdocs_folder_id,
          },
        });
        invalidateClientCapabilities(queryClient, tenantId);
      }}
    >
      <Form.Item
        name="gdocs_auto_upload_enabled"
        label="Auto-export to Google Docs"
        valuePropName="checked"
        extra={`When enabled, completed blog posts are automatically exported as Google Docs to this ${COPY.entity}'s Drive folder.`}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="gdocs_folder_id"
        label="Google Drive Workspace Folder ID"
        extra="The Google Drive folder ID shared with the service account. Leave blank to use the default shared folder."
        style={{ marginBottom: 8 }}
      >
        <Input placeholder="e.g. 1nBh0j0HMVdsSQ6rrlmCSlc_ZeO6eFqAd" />
      </Form.Item>
      <Button size="small" type="primary" htmlType="submit" loading={updateSettings.isPending}>
        Save
      </Button>
    </Form>
  );
}

/** R2 — "Last checked Nh/Nm ago" from the OAuth health-check timestamp. */
function lastCheckedSuffix(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `Last checked ${minutes}m ago`;
  return `Last checked ${Math.round(minutes / 60)}h ago`;
}

export default function IntegrationsSection() {
  const tenantId = useTenantStore((s) => s.currentTenant);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const userProfile = useWorkspaceStore((st) => st.userProfile);
  const currentWorkspace = useWorkspaceStore((st) => st.currentWorkspace);

  const {
    capabilities,
    isLoading: capsLoading,
    isError: capsError,
    dataUpdatedAt: capsUpdatedAt,
  } = useClientCapabilities(tenantId);
  // R5: the agency OAuth status now lives in the capability result — read it
  // once, early, so the latch effect below and the Ads resolution both see it.
  const oauthStatus = capabilities?.googleAds?.oauthStatus ?? null;

  // The same ['tenantSettings', tenantId] key the legacy page uses (the
  // WP/disconnect cache patches target it). Queried directly rather than
  // via useTenantSettings so this section does not depend on AuthContext.
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenantSettings', tenantId],
    queryFn: () => (tenantId ? apiService.getTenant(tenantId) : null),
    enabled: !!tenantId,
    staleTime: 60_000,
  });

  // Management tier + agency OAuth status now come from useClientCapabilities
  // (R5 — the join moved inside the hook). The section consumes the hook only;
  // there are NO side-join queries here (gate: grep + test).

  // Did we arrive here straight from the Google Ads OAuth callback?
  // Captured during RENDER, not in an effect — the cleanup effect below
  // deletes the params, and the detail mounts later (on demand), so only
  // a render-time capture survives. (Legacy had the same constraint
  // against GoogleAdsOAuthCard's child effect.)
  const [arrivedFromGoogleAdsConnect] = useState(
    () => searchParams.get('google_ads_connected') === '1',
  );
  const [autoDetectAttempted, setAutoDetectAttempted] = useState(false);

  // The durable just-connected line: latched on the OAuth return, cleared
  // once the status query confirms connected. Rendered in the cascade's
  // Connection layer — the pill + this line are the record, not the toast.
  const [justConnectedLine, setJustConnectedLine] = useState<string | null>(null);
  const [oauthErrorLine, setOauthErrorLine] = useState<string | null>(null);

  // OAuth callback feedback (EXTRACTED from GoogleAdsOAuthCard, wording
  // verbatim), surfaced once, then the params are cleaned exactly like the
  // legacy so the notification doesn't fire again on every render.
  useEffect(() => {
    const ok = searchParams.get('google_ads_connected');
    const err = searchParams.get('google_ads_error');
    if (ok) {
      const level = searchParams.get('level');
      const line =
        level === 'agency'
          ? `Google Ads MCC connected — covers all ${COPY.entity}s under the manager account.`
          : `Google Ads connected for this ${COPY.entity}.`;
      message.success(line);
      // The hook now owns the OAuth status (R5) — invalidate the ONE
      // capability key so the pill + factual line re-resolve immediately.
      invalidateClientCapabilities(queryClient, tenantId as string);
      setJustConnectedLine(line);
    } else if (err) {
      message.error({
        content: `Google Ads connection failed: ${decodeURIComponent(err)}`,
        duration: 8,
      });
      setOauthErrorLine(decodeURIComponent(err));
    }
    if (ok || err) {
      const next = new URLSearchParams(searchParams.toString());
      next.delete('google_ads_connected');
      next.delete('google_ads_error');
      next.delete('level');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient, tenantId]);

  // The latch holds only until the status query confirms connected.
  useEffect(() => {
    if (justConnectedLine && oauthStatus?.connected) {
      setJustConnectedLine(null);
    }
  }, [justConnectedLine, oauthStatus?.connected]);

  const [wpLockInOpen, setWpLockInOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnectWordPress = async () => {
    if (!tenantId) return;
    setDisconnecting(true);
    try {
      await apiService.disconnectWordPress(tenantId);
      queryClient.setQueryData(['tenantSettings', tenantId], (old: TenantSettings | null) =>
        old ? { ...old, wp_connected: false } : old,
      );
      notifyWordPressConnectionWritten(queryClient, tenantId, false);
      message.success('WordPress disconnected');
      setWpLockInOpen(false);
    } catch {
      message.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!tenantId) {
    return (
      <SectionPage sectionId="integrations">
        <p className="text-sm text-ink-2">{COPY.chooseEntity}</p>
      </SectionPage>
    );
  }

  // Initial unverified load: stable placeholder — no spinner, no wrong claims.
  if (!capabilities) {
    return (
      <SectionPage sectionId="integrations">
        {capsLoading ? <div aria-hidden="true" className="h-40" /> : null}
      </SectionPage>
    );
  }

  const staleSuffix = staleSuffixFor({ isError: capsError, dataUpdatedAt: capsUpdatedAt });
  const adsFocus = searchParams.get('focus') === 'google-ads';
  const adsPlanIncluded = capabilities.productPackage !== 'seo';

  // --- Google Ads pill + factual line. The STATE is the hook's (R3: the
  // pill stays 3-state); the FACTUAL LINE differentiates pending/revoked/
  // transient here (presentation), reading the joined oauthStatus (R5).
  const adsStatus = oauthStatus?.status;
  const agencyConnected = capabilities.googleAds.agencyConnected;
  const customerMapped = capabilities.googleAds.customerMapped;
  const adsState = capabilities.googleAds.state;
  // R2 (GA-S1): agency-unavailable is ONE composition — transient-down AND
  // revoked. (pending is transient: the poll resolves it; no composition.)
  const agencyUnavailable = !agencyConnected || adsStatus === 'revoked';
  let adsFactualLine: string;
  if (adsStatus === 'pending') {
    adsFactualLine = COPY.cards.adsPending;
  } else if (adsStatus === 'revoked') {
    adsFactualLine = COPY.cards.adsRevoked;
  } else if (agencyConnected && customerMapped) {
    const acct = `Account ${formatGoogleAdsCustomerId(tenantSettings?.google_ad_customer_id)}`;
    const checked = lastCheckedSuffix(oauthStatus?.last_health_check_at);
    adsFactualLine = checked ? `${acct} · ${checked}` : acct;
  } else if (agencyConnected) {
    adsFactualLine = COPY.cards.mappingRequired;
  } else {
    adsFactualLine = COPY.cards.agencyConnectionRequired;
  }

  // --- WordPress
  const wpState = capabilities.wordpress.state;
  const wpFactualLine =
    wpState === 'connected'
      ? `Key saved · ${tenantId}`
      : wpState === 'action_needed'
        ? 'Key entered, never verified'
        : 'No key saved';
  const wpPrimaryAction =
    wpState === 'not_set_up'
      ? { label: 'Connect WordPress', onClick: () => {} }
      : wpState === 'action_needed'
        ? { label: 'Fix connection', onClick: () => {} }
        : undefined;

  // --- Google Docs
  const gdocsConfigured = capabilities.googleDocs.configured;
  const gdocsFactualLine = gdocsConfigured
    ? [
        tenantSettings?.gdocs_auto_upload_enabled ? 'Auto-export on' : 'Auto-export off',
        tenantSettings?.gdocs_folder_id ? 'Folder set' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Not configured';

  return (
    <SectionPage sectionId="integrations">
      {oauthErrorLine ? (
        <p className="mb-3 flex items-center gap-2 text-xs text-amber-dark">
          <span>{`Google Ads connection failed: ${oauthErrorLine}`}</span>
          <button
            type="button"
            onClick={() => setOauthErrorLine(null)}
            className="underline decoration-warm-border-strong underline-offset-2"
          >
            {COPY.layoutNoticeDismiss}
          </button>
        </p>
      ) : null}

      {adsFocus && !adsPlanIncluded ? (
        <p className="mb-3 text-sm text-ink-2">{COPY.cards.adsPlanEmpty}</p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {adsPlanIncluded ? (
          <IntegrationCard
            title={COPY.cards.objectNames.googleAds}
            state={adsState}
            staleSuffix={staleSuffix}
            factualLine={adsFactualLine}
            degradedWarning={
              oauthStatus?.last_error ? `Last error: ${oauthStatus.last_error}` : undefined
            }
            scopeLine={COPY.cards.scopeGoogleAds}
            primaryAction={
              adsStatus === 'pending' || adsState === 'connected'
                ? undefined
                : agencyUnavailable
                  ? {
                      label: COPY.cards.mapAccount,
                      disabled: true,
                      disabledReason:
                        adsStatus === 'revoked'
                          ? COPY.cards.requiresAgencyReconnect
                          : oauthStatus == null
                            ? COPY.cards.noAgencyConnection
                            : COPY.cards.requiresAgencyConnection,
                      onClick: () => {},
                    }
                  : { label: COPY.cards.mapAccount, onClick: () => {} }
            }
            scopeAction={
              agencyUnavailable && isElevatedRole(userProfile?.platform_role)
                ? { label: COPY.cards.manage, to: COPY.adminPanel.integrationsPath }
                : undefined
            }
            detail={
              adsStatus === 'revoked' || adsStatus === 'pending' ? undefined : (
                <GoogleAdsCascadeDetail
                  tenantId={tenantId}
                  workspaceId={String(currentWorkspace?.id ?? '')}
                  productPackage={capabilities.productPackage}
                  status={oauthStatus ?? undefined}
                  tier={capabilities.googleAds.tier ?? null}
                  justConnectedLine={justConnectedLine}
                  autoDetect={arrivedFromGoogleAdsConnect && !autoDetectAttempted}
                  onAutoDetectAttempted={() => setAutoDetectAttempted(true)}
                  tenantSettings={tenantSettings}
                />
              )
            }
          />
        ) : null}

        <IntegrationCard
          title={COPY.cards.objectNames.wordpress}
          state={wpState}
          verified={capabilities.wordpress.verified}
          staleSuffix={staleSuffix}
          factualLine={wpFactualLine}
          scopeLine={COPY.cards.scopeWordPress}
          primaryAction={wpPrimaryAction}
          secondaryActions={
            wpState === 'connected'
              ? [{ label: COPY.cards.disconnect, onClick: () => setWpLockInOpen(true) }]
              : undefined
          }
          detail={<WordPressConnectionDetail tenantId={tenantId} />}
        />

        <IntegrationCard
          title={COPY.cards.objectNames.googleDocs}
          state={capabilities.googleDocs.state}
          verified={capabilities.googleDocs.verified}
          staleSuffix={staleSuffix}
          factualLine={gdocsFactualLine}
          scopeLine={COPY.cards.scopeGoogleDocs}
          primaryAction={{
            label: gdocsConfigured ? 'Edit settings' : 'Set up export',
            onClick: () => {},
          }}
          detail={<GoogleDocsDetail tenantId={tenantId} tenantSettings={tenantSettings} />}
        />
      </div>

      <DisconnectLockInDialog
        open={wpLockInOpen}
        onOpenChange={setWpLockInOpen}
        title={COPY.cards.disconnectWordPressTitle}
        blastRadius={COPY.cards.disconnectWordPressBlast(tenantId)}
        onConfirm={handleDisconnectWordPress}
        busy={disconnecting}
      />
    </SectionPage>
  );
}
